// App-wide tooltip in the console style, replacing the browser's own `title`
// popup on every page (old and new) without touching their markup.
//
// While the pointer (or keyboard focus) is on an element with a `title`, the text
// is parked in `data-ff-tip` so the native popup never appears, and shown in one
// shared bubble (.ff-tip, styled in App.vue). On leave the `title` goes back, so
// nothing that reads it later sees a difference. Touch is left alone.

const SHOW_DELAY = 350 // ms of hovering before the first tooltip shows
const WARM_FOR = 400 // moving to the next item within this window shows at once
const GAP = 8 // px between the element and the bubble
const EDGE = 6 // px kept free at the window edges

let bubble = null
let current = null
let timer = null
let hiddenAt = 0
let clicked = null // pressed while hovered: stays quiet until the pointer moves on

function ensureBubble () {
  if (!bubble) {
    bubble = document.createElement('div')
    bubble.className = 'ff-tip'
    bubble.id = 'ff-tip'
    bubble.setAttribute('role', 'tooltip')
  }
  if (!bubble.isConnected) document.body.appendChild(bubble)
  return bubble
}

function targetOf (node) {
  return node && node.nodeType === 1 && node.closest ? node.closest('[title], [data-ff-tip]') : null
}

// Park the title so the browser has nothing to show. All attribute changes
// happen here, at pointer arrival, never later: a capture button pre-renders on
// hover and would otherwise see its area as changed (helpers/captureImage.js).
function take (el) {
  const title = el.getAttribute('title')
  if (title !== null) {
    el.setAttribute('data-ff-tip', title)
    el.removeAttribute('title')
  }
  if (!el.hasAttribute('aria-describedby')) el.setAttribute('aria-describedby', 'ff-tip')
}

function giveBack (el) {
  const text = el.getAttribute('data-ff-tip')
  // a re-render may already have put a fresh title back; keep that one
  if (text !== null && !el.hasAttribute('title')) el.setAttribute('title', text)
  el.removeAttribute('data-ff-tip')
  if (el.getAttribute('aria-describedby') === 'ff-tip') el.removeAttribute('aria-describedby')
}

function place (el) {
  const r = el.getBoundingClientRect()
  const b = bubble.getBoundingClientRect()
  let top = r.bottom + GAP
  let side = 'below'
  if (top + b.height > window.innerHeight - EDGE && r.top - GAP - b.height >= EDGE) {
    top = r.top - GAP - b.height
    side = 'above'
  }
  const centre = r.left + r.width / 2
  const left = Math.min(Math.max(centre - b.width / 2, EDGE), window.innerWidth - b.width - EDGE)
  bubble.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`
  bubble.style.setProperty('--ff-tip-arrow', `${Math.round(Math.min(Math.max(centre - left, 10), b.width - 10))}px`)
  bubble.dataset.side = side
}

function show (el) {
  const text = (el.getAttribute('data-ff-tip') || '').trim()
  if (!text || !el.isConnected) return
  ensureBubble().textContent = text
  place(el)
  bubble.classList.add('is-on')
}

function hide () {
  clearTimeout(timer)
  if (bubble && bubble.classList.contains('is-on')) {
    bubble.classList.remove('is-on')
    hiddenAt = Date.now()
  }
}

function enter (el) {
  if (el === current) return
  leave()
  current = el
  take(el)
  if (el === clicked) return
  clicked = null
  const warm = Date.now() - hiddenAt < WARM_FOR
  timer = setTimeout(() => { if (current === el) show(el) }, warm ? 0 : SHOW_DELAY)
}

function leave () {
  hide()
  if (current) giveBack(current)
  current = null
}

function onOver (event) {
  if (event.pointerType === 'touch') return
  const el = targetOf(event.target)
  if (el) enter(el)
  else {
    leave()
    // an overlay (e.g. loading) passing over does not count as moving on
    if (!(event.target && event.target.closest && event.target.closest('.ff-loading'))) clicked = null
  }
}

function onDown () {
  hide()
  clicked = current
}

function onOut (event) {
  if (current && !(event.relatedTarget && current.contains(event.relatedTarget))) leave()
}

function onFocusIn (event) {
  const el = targetOf(event.target)
  if (el && event.target.matches && event.target.matches(':focus-visible')) enter(el)
}

export function installTooltips () {
  document.addEventListener('pointerover', onOver, true)
  document.addEventListener('pointerout', onOut, true)
  document.addEventListener('focusin', onFocusIn, true)
  document.addEventListener('focusout', leave, true)
  // anything that moves or acts on the page closes the bubble
  document.addEventListener('pointerdown', onDown, true)
  document.addEventListener('wheel', hide, { capture: true, passive: true })
  document.addEventListener('scroll', hide, true)
  document.addEventListener('keydown', hide, true)
  window.addEventListener('blur', leave)
}

export default {
  install () {
    installTooltips()
  },
}
